/**
 * 見積のお客様宛メール本文のテンプレートと追加文
 * テンプレート（kind: template）は本文全部。共通用の標準が 1 つ（userId が null）＋ユーザーごとの追加分。
 * 追加文（kind: snippet）は本文のカーソル位置に差し込む文章。共通用とユーザーごと。
 * どちらも本文の {{キー}} を見積の値に置き換える
 */
import * as backend from '@libs/backend'
import * as utils from '@libs/utils'

// 種類
export const KIND_TEMPLATE = 'template'
export const KIND_SNIPPET = 'snippet'
export const KINDS = [
  { key: KIND_TEMPLATE, label: 'テンプレート', note: '自分用のテンプレートを作っていない人は、「共通用」がデフォルトになります。' },
  { key: KIND_SNIPPET, label: '追加文', note: '本文の好きな場所に差し込む文章' }
]

// 本文に書ける印。編集モーダルのボタンと置き換えで使う
export const PLACEHOLDERS = [
  { key: '得意先名', note: '敬称が「様」のときは空になり、印だけの行なら行ごと消えます' },
  { key: '担当者名', note: 'お客様のご担当者。末尾の「様」は外します。無ければ「ご担当者」' },
  { key: '物件名', note: '無ければ「物件名〇〇」' },
  { key: '要件', note: '無ければ「要件〇〇」' },
  { key: 'アポロ担当者姓', note: '見積の担当者の姓' },
  { key: 'アポロ担当者名', note: '見積の担当者の氏名（カナ付きのまま）' },
  { key: 'アポロ担当者メール', note: '見積の担当者のメールアドレス（送信元）' }
]

// 標準テンプレートが DB に無いときに使う組み込みの文面（DB の標準と同じ）
export const DEFAULT_BODY = [
  '{{得意先名}}',
  '{{担当者名}} 様',
  '',
  'いつも大変お世話になっております。',
  'アポロ管財の{{アポロ担当者姓}}でございます。',
  '',
  'この度はお見積もりのご依頼をいただき誠にありがとうございます。',
  '{{物件名}} の お見積書（{{要件}}）を添付にてお送りいたします。',
  'ご査収のほどよろしくお願い申し上げます。',
  '',
  '',
  'アポロ管財株式会社',
  '〒201-0015 東京都狛江市猪方3-36-3',
  'TEL：03-5438-8804',
  'FAX：03-5438-8809',
  '担当：{{アポロ担当者名}}',
  'email：{{アポロ担当者メール}}',
  '＊＊＊＊＊＊＊＊＊＊＊＊＊＊＊＊＊＊＊＊＊＊＊＊＊＊＊＊',
  '◆ホームページをリニューアルいたしました。',
  'アポロ管財のホームページはこちら',
  'https://apollo-kanzai.co.jp/',
  '',
  '◆シェアNo.1の巡回清掃「ラウンドリビングサービス」',
  'https://youtu.be/K9resP0AWn0'
].join('\n')

// 共通用・他人のものを編集できる条件の説明（画面に出す。先頭に「共通用」か「他人の」を付ける）
export const EDIT_RULE = 'を編集できるのは、管理者権限者と、受託営業部でグループ 4 以上の人です。'
export const STANDARD_EDIT_RULE = '共通用' + EDIT_RULE
export const OTHERS_EDIT_RULE = '他人の' + EDIT_RULE

/**
 * 共通用（標準テンプレート・共通用の追加文）を編集できるか
 * 管理者権限あり、またはメインの部署が「受託営業」かつグループ 4 以上
 * @param {*} store this.$store
 */
export function canEditStandard(store) {
  const auth = store.getters.auth
  if (auth && auth.adminAuth) {
    return true
  }
  const login = store.getters.loginUserData
  const user = store.getters.user
  const dep = login && login.departmentName ? login.departmentName : ''
  return dep.indexOf('受託営業') > -1 && utils.isGroup4(user ? user.group_num : null)
}

/**
 * キーを本文に書く形にする
 * @param {*} key キー（例：得意先名）
 * @returns 例：{{得意先名}}
 */
export function mark(key) {
  return '{{' + key + '}}'
}

/**
 * 一覧（共通用＋そのユーザーのもの。テンプレートも追加文も全部）。共通用が先頭
 * API: user/getMailTemplateList
 * @param {*} userId 持ち主のユーザーID
 * @param {*} ownerOnly true なら共通用を除いてその人のものだけ（他人のを確認するとき）
 */
export async function getList(userId, ownerOnly) {
  const res = await backend.searchData('user/getMailTemplateList', { userId, ownerOnly: !!ownerOnly })
  if (res && res.data && res.data.data) {
    return res.data.data
  }
  return []
}

/**
 * テンプレートか追加文を 1 つでも登録している人の一覧（自分は除く。退職者も除く）
 * API: user/getMailTemplateOwnerList
 * @param {*} userId ログインユーザーID
 * @returns [{ userId, name }]
 */
export async function getOwnerList(userId) {
  const res = await backend.searchData('user/getMailTemplateOwnerList', { userId })
  if (res && res.data && res.data.data) {
    return res.data.data
  }
  return []
}

/**
 * 保存（id があれば更新、無ければ追加）
 * API: user/saveMailTemplate
 * @param {*} data { id, kind, userId（持ち主。共通用は null）, name, body, isDefault, loginUserId }
 * @returns 保存した行の id
 */
export async function save(data) {
  const res = await backend.postData('user/saveMailTemplate', data)
  if (res && res.data) {
    return res.data.data
  }
  return null
}

/**
 * 削除
 * API: user/deleteMailTemplate
 * @param {*} id ID
 * @param {*} userId ログインユーザーID
 * @param {*} allowOthers 共通用・他人のものを消してよい人（権限者）なら true
 */
export async function remove(id, userId, allowOthers) {
  await backend.postData('user/deleteMailTemplate', { id, userId, allowOthers })
}

/**
 * 一覧からテンプレートだけ
 */
export function templatesOf(list) {
  return (list || []).filter((t) => t.kind == KIND_TEMPLATE)
}

/**
 * 一覧から追加文だけ
 */
export function snippetsOf(list) {
  return (list || []).filter((t) => t.kind == KIND_SNIPPET)
}

/**
 * 見積画面を開いたときに最初に選ぶテンプレート
 * 自分のデフォルト → 共通用の標準 → 先頭
 * @param {*} list 一覧
 */
export function getInitialTemplate(list) {
  const templates = templatesOf(list)
  if (!templates.length) {
    return null
  }
  return templates.find((t) => t.isDefault) || templates.find((t) => !t.userId) || templates[0]
}

/**
 * 本文の印を値に置き換える
 * 印だけの行が置き換えで空になったら、その行ごと消す（敬称が「様」のときの得意先名など）
 * 知らない印はそのまま残す（間違いに気付けるように）
 * @param {*} body テンプレート本文
 * @param {*} values { 得意先名: '…', 担当者名: '…', … }
 * @returns 改行は \r\n
 */
export function buildMailText(body, values) {
  const lines = (body || '').replace(/\r\n/g, '\n').split('\n')
  const res = []
  for (const line of lines) {
    const hasMark = /\{\{[^{}]+\}\}/.test(line)
    // 印以外に文字が無い行か
    const onlyMarks = hasMark && line.replace(/\{\{[^{}]+\}\}/g, '').trim() === ''
    const replaced = line.replace(/\{\{([^{}]+)\}\}/g, (m, key) => {
      return key in values ? (values[key] || '') : m
    })
    if (onlyMarks && replaced.trim() === '') {
      continue
    }
    res.push(replaced)
  }
  return res.join('\r\n')
}

/**
 * 本文に書かれている印のうち、置き換えられないものを返す
 * @param {*} body テンプレート本文
 * @returns 知らない印の配列（例：['{{物件}}']）
 */
export function findUnknownMarks(body) {
  const known = PLACEHOLDERS.map((p) => p.key)
  const found = (body || '').match(/\{\{[^{}]+\}\}/g) || []
  const unknown = found.filter((m) => !known.includes(m.slice(2, -2)))
  return [...new Set(unknown)]
}
